import {Component, DestroyRef, inject, Injector, ChangeDetectionStrategy, signal} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatMenuModule} from "@angular/material/menu"
import {AccordionStyle, UiStyle} from "@cm/template-nodes"
import {ConfigMenuComponent} from "@app/common/components/viewers/configurator/config-menu/config-menu.component"
import {ConfigMenuService} from "@app/common/components/viewers/configurator/config-menu/services/config-menu.service"
import {createMaterialReferenceFromLegacyId} from "@app/common/components/viewers/configurator/helpers/parameters"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-select-variation",
    imports: [ConfigMenuComponent, MatMenuModule],
    templateUrl: "./template-select-variation.component.html",
    styleUrl: "./template-select-variation.component.scss",
})
export class TemplateSelectVariationComponent {
    private readonly sceneManagerService = inject(SceneManagerService)
    private readonly threeSceneManagerService = inject(ThreeSceneManagerService)
    private readonly configMenuService = inject(ConfigMenuService)
    private readonly destroyRef = inject(DestroyRef)
    private readonly injector = inject(Injector)

    readonly $uiStyle = signal<UiStyle>("icons")
    readonly availableUiStyles: {value: UiStyle; label: string}[] = [
        {value: "default", label: "Default"},
        {value: "icons", label: "Icons"},
        {value: "accordion", label: "Accordion"},
        {value: "mobileTabs", label: "Mobile Tabs"},
        {value: "hidden", label: "Hidden"},
    ]

    readonly $accordionStyle = signal<AccordionStyle>("autoClose")
    readonly availableAccordionStyles: {value: AccordionStyle; label: string}[] = [
        {value: "autoClose", label: "Auto Close"},
        {value: "manualClose", label: "Manual Close"},
        {value: "stepInto", label: "Step Into"},
        {value: "stepThrough", label: "Step Through"},
    ]

    ngOnInit() {
        this.sceneManagerService.descriptorList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((descriptors) => {
            this.configMenuService.setInterface(descriptors)
        })

        this.configMenuService.configSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((config) => {
            this.sceneManagerService.setTemplateParameter(config.config.props.id, config.variant.id)
            this.syncConfigChangesAndNotify()
        })

        this.configMenuService.materialSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (material) => {
            const materialReference = await createMaterialReferenceFromLegacyId(material.legacyId, this.injector)
            this.sceneManagerService.setTemplateParameter(material.input.props.id, materialReference)
        })
    }

    labelForUiStyle(value: UiStyle): string {
        return this.availableUiStyles.find((opt) => opt.value === value)?.label ?? value
    }

    labelForAccordionStyle(value: AccordionStyle): string {
        return this.availableAccordionStyles.find((opt) => opt.value === value)?.label ?? value
    }

    private async syncConfigChangesAndNotify() {
        await this.threeSceneManagerService.sync(true)
        this.configMenuService.setSynchronizing(false)
    }
}
